<script>
import EmptyState from "@/components/widgets/empty-state";

/**
 * Lista responsiva (piloto): tabela a partir de md (768px) e cards empilhados no celular.
 * Mesma ideia do `stacked="md"` do b-table, mas com card de verdade (título + pares
 * rótulo/valor) em vez de uma tabela "desmontada".
 *
 * fields: [{ key, label, titulo?: true, mobile?: false, class?: '', align?: 'right' }]
 *   - `titulo`: vira o cabeçalho do card no celular (use em 1 campo).
 *   - `mobile: false`: some do card no celular (continua na tabela).
 *
 * Slots: `cell(<key>)` com { item, value } — mesma convenção do b-table.
 *        `card-footer` com { item } — rodapé opcional do card no celular.
 */
export default {
  name: "StResponsiveList",
  components: { EmptyState },
  props: {
    fields: { type: Array, required: true },
    items: { type: Array, default: () => [] },
    keyField: { type: String, default: "" },
    caption: { type: String, default: "" },
    emptyTitle: { type: String, default: "Nada por aqui ainda" },
    emptyText: { type: String, default: "" },
    emptyIcon: { type: String, default: "bx bx-folder-open" },
    rowClass: { type: Function, default: null },
  },
  computed: {
    campoTitulo() {
      return this.fields.find((f) => f.titulo) || null;
    },
    camposCard() {
      return this.fields.filter((f) => !f.titulo && f.mobile !== false);
    },
  },
  methods: {
    chave(item, i) {
      return this.keyField && item[this.keyField] != null ? item[this.keyField] : i;
    },
    classeLinha(item) {
      return this.rowClass ? this.rowClass(item) : null;
    },
  },
};
</script>

<template>
  <div class="st-rl">
    <EmptyState v-if="!items.length" size="sm" :icon="emptyIcon" :title="emptyTitle" :text="emptyText" />

    <template v-else>
      <!-- Desktop / tablet -->
      <div class="table-responsive d-none d-md-block">
        <table class="table table-hover st-rl-table mb-0">
          <caption v-if="caption" class="sr-only">{{ caption }}</caption>
          <thead>
            <tr>
              <th
                v-for="f in fields"
                :key="f.key"
                scope="col"
                :class="[f.class, f.align === 'right' ? 'text-right' : '']"
              >{{ f.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, i) in items" :key="chave(item, i)" :class="classeLinha(item)">
              <td
                v-for="f in fields"
                :key="f.key"
                :class="[f.class, f.align === 'right' ? 'text-right' : '']"
                :data-campo="f.key"
              >
                <slot :name="'cell(' + f.key + ')'" :item="item" :value="item[f.key]">{{ item[f.key] }}</slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Celular -->
      <ul class="st-rl-cards d-md-none" :aria-label="caption || null">
        <li v-for="(item, i) in items" :key="chave(item, i)" class="st-rl-card" :class="classeLinha(item)">
          <div v-if="campoTitulo" class="st-rl-card-title" :data-campo="campoTitulo.key">
            <slot :name="'cell(' + campoTitulo.key + ')'" :item="item" :value="item[campoTitulo.key]">{{ item[campoTitulo.key] }}</slot>
          </div>
          <dl class="st-rl-card-body">
            <div v-for="f in camposCard" :key="f.key" class="st-rl-card-row">
              <dt>{{ f.label }}</dt>
              <dd :data-campo="f.key">
                <slot :name="'cell(' + f.key + ')'" :item="item" :value="item[f.key]">{{ item[f.key] }}</slot>
              </dd>
            </div>
          </dl>
          <div v-if="$scopedSlots['card-footer']" class="st-rl-card-footer">
            <slot name="card-footer" :item="item"></slot>
          </div>
        </li>
      </ul>
    </template>
  </div>
</template>

<style scoped>
.st-rl-table th {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--st-text-muted);
  white-space: nowrap;
  border-top: 0;
}
.st-rl-table td {
  vertical-align: middle;
}
.st-rl-cards {
  margin: 0;
  padding: 0;
  list-style: none;
}
.st-rl-card {
  padding: 0.75rem 0.875rem;
  border: 1px solid var(--st-border-soft);
  border-radius: var(--st-radius);
  background: var(--st-surface);
}
.st-rl-card + .st-rl-card {
  margin-top: 0.5rem;
}
.st-rl-card-title {
  margin-bottom: 0.375rem;
  font-weight: 600;
  color: var(--st-text);
}
.st-rl-card-body {
  margin: 0;
}
.st-rl-card-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.1875rem 0;
  font-size: 0.875rem;
}
.st-rl-card-row dt {
  font-weight: 400;
  color: var(--st-text-muted);
}
.st-rl-card-row dd {
  margin: 0;
  text-align: right;
  color: var(--st-text);
  min-width: 0;
  overflow-wrap: anywhere;
}
.st-rl-card-footer {
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--st-border-soft);
}
</style>
